import React from "react";
import { Link } from "react-router-dom";
import { 
  HiArrowRight,
  HiCheckCircle, 
  HiDocumentText, 
  HiBriefcase,
  HiChatBubbleLeftRight,
  HiChevronRight,
  HiStar
} from "react-icons/hi2";
import { ROUTES } from "../constants";
import TredningPostsm from "../components/TrendingPosts/TrendingPostsm";

const PublicHomePage = () => {
  return (
    <div className="flex flex-col min-h-screen bg-white selection:bg-sky-100 selection:text-sky-900 font-sans text-slate-900">
      
      {/* --- 1. THE PROFESSIONAL HERO --- */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-[#0A0F1C]">
        {/* Subtle Grid Background for that "Engineering/Precision" feel */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          {/* Release Badge */}
          <Link to={ROUTES.login} className="inline-flex items-center gap-2 px-4 py-2 mb-8 text-xs font-semibold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded-full hover:bg-sky-500/20 transition-colors">
            <span className="flex h-2 w-2 rounded-full bg-sky-500 animate-pulse" />
            CampusIRA is now live for your institution <HiChevronRight size={14} />
          </Link>

          {/* Authoritative Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-bold tracking-tight leading-[1.05] mb-8 text-white">
            The definitive platform <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-indigo-400">
              for campus success.
            </span>
          </h1>

          {/* Clear, Concise Subheadline */}
          <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed max-w-3xl mx-auto mb-10">
            Stop relying on scattered links and guesswork. CampusIRA centralizes verified interview insights, curated study materials, and exclusive job opportunities in one secure ecosystem.
          </p>

          {/* Primary & Secondary CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
            <Link
              to={ROUTES.login}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-white text-slate-950 px-8 py-4 rounded-full font-bold text-base hover:bg-slate-100 transition-all shadow-lg shadow-white/5"
            >
              Access the Platform
            </Link>
            <a href="#features" className="w-full sm:w-auto flex items-center justify-center gap-2 bg-transparent text-white border border-slate-700 hover:border-slate-500 px-8 py-4 rounded-full font-semibold text-base transition-all">
              Explore Features
            </a>
          </div>

          {/* THE PRODUCT PREVIEW MOCKUP (Crucial for Professional Feel) */}
          <div className="relative mx-auto max-w-5xl">
            {/* Ambient Glow behind the mockup */}
            <div className="absolute -inset-1 bg-gradient-to-r from-sky-500 to-indigo-500 rounded-t-xl blur-2xl opacity-20" />
            
            <div className="relative rounded-t-xl bg-slate-900 border-t border-l border-r border-slate-800 shadow-2xl overflow-hidden flex flex-col items-center justify-start h-[300px] lg:h-[400px]">
              {/* Browser/App Header Bar */}
              <div className="w-full bg-slate-950/50 border-b border-slate-800 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-slate-700" />
                  <div className="w-3 h-3 rounded-full bg-slate-700" />
                  <div className="w-3 h-3 rounded-full bg-slate-700" />
                </div>
                <div className="ml-4 h-5 w-48 bg-slate-800/50 rounded-md" />
              </div>
              
              {/* Abstract Representation of the Feed/Dashboard */}
              <div className="w-full p-6 lg:p-10 opacity-60 pointer-events-none select-none mask-image-bottom">
                 <TredningPostsm />
              </div>
              
              {/* Fade out bottom overlay */}
              <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-slate-900 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* --- 2. LOGO CLOUD / TRUST BANNER --- */}
      <section className="border-b border-slate-200 bg-white py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 opacity-60 grayscale">
          <p className="text-sm font-semibold text-slate-500 tracking-wider uppercase">Verified insights for companies like:</p>
          <div className="flex gap-8 font-bold text-xl text-slate-400">
            <span>Amazon</span>
            <span>TCS</span>
            <span>Infosys</span>
            <span>Cisco</span>
            <span>Wipro</span>
          </div>
        </div>
      </section>

      {/* --- 3. THE CORE FEATURES (Alternating Layout) --- */}
      <section id="features" className="py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 space-y-32">
          
          {/* Feature 1: Interviews */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative bg-slate-50 rounded-3xl p-8 border border-slate-100 h-[400px] flex items-center justify-center overflow-hidden">
               <div className="absolute inset-0 bg-grid-slate-200/[0.04] bg-[size:20px_20px]" />
               <div className="w-full max-w-sm bg-white p-6 rounded-2xl shadow-xl border border-slate-100 relative z-10 -rotate-2 hover:rotate-0 transition-transform duration-500">
                 <div className="flex items-center gap-3 mb-4">
                   <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center text-sky-600"><HiChatBubbleLeftRight size={20}/></div>
                   <div>
                     <p className="text-sm font-bold">SDE-1 Interview Experience</p>
                     <p className="text-xs text-slate-500">2 hours ago • Verified Senior</p>
                   </div>
                 </div>
                 <div className="space-y-2">
                   <div className="h-2 w-full bg-slate-100 rounded" />
                   <div className="h-2 w-5/6 bg-slate-100 rounded" />
                   <div className="h-2 w-4/6 bg-slate-100 rounded" />
                 </div>
               </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-sky-600 font-semibold mb-4 text-sm">
                <HiChatBubbleLeftRight size={20} /> Real Experiences
              </div>
              <h2 className="text-4xl font-bold tracking-tight text-slate-900 mb-6">Unfiltered Interview Intel.</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Get the exact details of what happened in the interview room. Read comprehensive, campus-specific threads covering technical rounds, HR questions, and panel expectations.
              </p>
              <ul className="space-y-4">
                {["Detailed round-by-round breakdowns", "Company-specific question patterns", "Tips from selected candidates"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <HiCheckCircle className="text-sky-500" size={24} /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Feature 2: Resources */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-indigo-600 font-semibold mb-4 text-sm">
                <HiDocumentText size={20} /> Academic Archive
              </div>
              <h2 className="text-4xl font-bold tracking-tight text-slate-900 mb-6">The Resource Ground-Truth.</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Access a centralized, meticulously organized vault of Previous Year Questions (PYQs), verified lab manuals, and handwritten notes tailored to your current semester.
              </p>
              <ul className="space-y-4">
                {["Curated for your specific syllabus", "High-yield topics for end-sems", "Downloadable and organized PDFs"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <HiCheckCircle className="text-indigo-500" size={24} /> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative bg-slate-50 rounded-3xl p-8 border border-slate-100 h-[400px] flex items-center justify-center overflow-hidden">
               <div className="absolute inset-0 bg-grid-slate-200/[0.04] bg-[size:20px_20px]" />
               <div className="w-full max-w-sm bg-white p-6 rounded-2xl shadow-xl border border-slate-100 relative z-10 rotate-2 hover:rotate-0 transition-transform duration-500">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Semester 5 Downloads</p>
                  <div className="space-y-3">
                     {[1,2,3].map(i => (
                       <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                         <div className="flex items-center gap-3">
                           <HiDocumentText className="text-slate-400" size={24} />
                           <div className="h-2 w-24 bg-slate-200 rounded" />
                         </div>
                         <div className="h-6 w-16 bg-slate-200 rounded-full" />
                       </div>
                     ))}
                  </div>
               </div>
            </div>
          </div>

          {/* Feature 3: Opportunities */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative bg-slate-50 rounded-3xl p-8 border border-slate-100 h-[400px] flex items-center justify-center overflow-hidden">
               <div className="absolute inset-0 bg-grid-slate-200/[0.04] bg-[size:20px_20px]" />
               <div className="w-full max-w-sm bg-slate-900 p-8 rounded-2xl shadow-2xl relative z-10 text-center">
                 <HiBriefcase className="text-emerald-400 mx-auto mb-4" size={48} />
                 <p className="text-white font-bold text-xl mb-2">Off-Campus Alert</p>
                 <p className="text-slate-400 text-sm mb-6">Looking for 2026 Batch Graduates.</p>
                 <button className="w-full py-3 bg-emerald-500 text-slate-900 font-bold rounded-lg">Apply via Referral</button>
               </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-emerald-600 font-semibold mb-4 text-sm">
                <HiBriefcase size={20} /> Career Growth
              </div>
              <h2 className="text-4xl font-bold tracking-tight text-slate-900 mb-6">Strategic Opportunities.</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Bridge the gap between preparation and employment. We surface hand-picked off-campus alerts, referral links, and job postings that actively seek out your campus profile.
              </p>
              <Link to={ROUTES.login} className="inline-flex items-center gap-2 text-emerald-600 font-bold hover:text-emerald-700 transition-colors">
                View Active Openings <HiArrowRight size={20} />
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* --- 4. CLEAN METRICS / STATS --- */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Empowering the student community.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { val: "5,000+", label: "Verified Student Accounts" },
              { val: "1,200+", label: "Shared Interview Experiences" },
              { val: "4.9/5", label: "Average Resource Rating", icon: <HiStar className="inline text-yellow-400 mb-1" /> }
            ].map((stat, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 text-center shadow-sm">
                 <div className="text-4xl font-black text-slate-900 mb-2 tracking-tight">
                    {stat.val} {stat.icon && stat.icon}
                 </div>
                 <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 5. PROFESSIONAL CTA --- */}
      <section className="py-32 bg-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Ready to gain your advantage?
          </h2>
          <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto">
            Join thousands of students who are already using CampusIRA to navigate their academics and secure their dream placements.
          </p>
          <Link
            to={ROUTES.login}
            className="inline-flex items-center gap-3 bg-slate-900 text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-slate-800 transition-colors shadow-xl"
          >
            Create your account <HiArrowRight size={20} />
          </Link>
          <p className="mt-6 text-sm text-slate-400 font-medium">Free forever for verified campus students.</p>
        </div>
      </section>

      {/* --- 6. MINIMALIST FOOTER --- */}
      <footer className="py-10 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm font-semibold text-slate-500">
            &copy; {new Date().getFullYear()} CampusIRA. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm font-medium text-slate-400">
            <a href="#" className="hover:text-slate-900 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-slate-900 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-slate-900 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicHomePage;